import { createDetailedBookingSOVFormConfig } from '../../config/createDetailedBookingSOVFormConfig';
import {
  DetailedBookingSOVData,
  DetailedBookingSOVPartialDataErrors,
  SlotOptionType,
  SlotPackage,
} from '../../interface/DetailedBookingInterface';
import { FormFieldWrapper } from '../FormFieldWrapper';
import { GP_SLOT_VALUES } from '../../constants/DetailedBookingConstants';
import { getSelectedSlotData } from '../../services/sovFormHelpers/helpers';
import { useTranslation } from 'react-i18next';
import { InfoFieldTextContainer } from '../../CreateBookingModal.styles';
import { GP_SLOT_FIELD } from '../../constants/FormFieldsConstants';

interface RenderMainFormProps {
  formData: DetailedBookingSOVData;
  handleInputChange: (field: string, value: string) => void;
  isContextualSelected: boolean;
  fieldErrors: DetailedBookingSOVPartialDataErrors;
  hasValidationBeenTriggered: boolean;
  slotOptions: SlotOptionType[];
  isLoadingSlots: boolean;
  roValue: number;
  slotPackagesData: SlotPackage[];
  selectedOption?: SlotOptionType;
  hideCommonFields: boolean;
  preSelectedSlotNames?: string[];
}

const SOVMainForm = ({
  formData,
  handleInputChange,
  isContextualSelected,
  fieldErrors,
  hasValidationBeenTriggered,
  slotOptions,
  isLoadingSlots,
  roValue,
  slotPackagesData = [],
  selectedOption,
  hideCommonFields = false,
  preSelectedSlotNames = [],
}: RenderMainFormProps) => {
  const { t } = useTranslation();

  const errorsToShow = hasValidationBeenTriggered
    ? fieldErrors
    : {
        entryNumber: '',
        gpSlot: '',
        sovPercentageForGP: '',
        valueBlockedByLineItem: '',
        contextualNode: '',
        sovPercentageForContextualGP: '',
        impressionBooked: '',
        valueBooked: '',
      };

  const filteredSlotOptions = slotOptions.filter(
    (opt) => !preSelectedSlotNames.includes(opt.value)
  );

  const formConfig = createDetailedBookingSOVFormConfig(
    formData,
    handleInputChange,
    isContextualSelected,
    errorsToShow,
    filteredSlotOptions,
    isLoadingSlots,
    hideCommonFields
  );

  const isNonContextual =
    selectedOption?.type === GP_SLOT_VALUES.NON_CONTEXTUAL;
  const isContextual = selectedOption?.type === GP_SLOT_VALUES.CONTEXTUAL;

  const selectedSlotData = isNonContextual
    ? getSelectedSlotData(formData.gpSlot, slotPackagesData, selectedOption)
    : null;

  const toShowSlotInfo = (id: string) =>
    id === GP_SLOT_FIELD && !!selectedSlotData;

  // this will only be done in case of gp sov
  const toShowAvailableRoValue = (id: string) =>
    id === 'value-blocked-field' && isContextual;

  return (
    <>
      {formConfig.map((field) => (
        <div key={field.id}>
          {toShowAvailableRoValue(field.id) && (
            <InfoFieldTextContainer>
              {t('available-ro-value-info', {
                roValue,
              })}
            </InfoFieldTextContainer>
          )}
          <FormFieldWrapper
            id={field.id}
            type={field.type}
            label={field.label}
            value={field.value}
            onChange={field.onChange}
            placeholder={field.placeholder}
            disabled={field.disabled}
            options={field.options}
            error={field.error}
          />
          {toShowSlotInfo(field.id) && (
            <InfoFieldTextContainer>
              (
              {t('available-slot-percentage-info', {
                availablePercentage: selectedSlotData?.percentageAvailable,
              })}
              )&nbsp;(
              {t('available-ro-value-info', {
                roValue,
              })}
              )
            </InfoFieldTextContainer>
          )}
        </div>
      ))}
    </>
  );
};

export default SOVMainForm;
